'use client'

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import { updateClarityConsent } from '@/lib/consent-manager'

/**
 * Shuts down marketing observability on entry to `/sites/**`.
 *
 * `ConditionalMarketingShell` already keeps the marketing chrome, consent
 * banner, GA4 SPA page views, Vercel Analytics, CTA/AI-referrer attribution
 * and the chat widget from ever mounting on `/sites`, and the GA4 and Clarity
 * loaders in the app root layout skip their initial page view / boot there.
 * That covers a direct entry.
 *
 * A client-side transition is the remaining case: a visitor who lands on a
 * marketing page first has already booted Clarity, and Sentry's replay
 * integration is configured with `replaysOnErrorSampleRate: 1.0`. Neither
 * unmounts on navigation, so the control plane would keep recording a session
 * that started on marketing. This stops both on arrival, using the shipped
 * stop paths rather than new ones:
 *
 *   - `updateClarityConsent('denied', true)` is the existing consent-manager
 *     helper; the `shouldStop` flag is what issues `clarity('stop')`.
 *   - `Sentry.getReplay()?.stop()` is the supported SDK call, re-exported from
 *     @sentry/nextjs. It is a no-op when no replay is running.
 *
 * Both are one-way for the session by design: nothing here restarts them if
 * the visitor navigates back out to marketing. That is the conservative
 * direction — the alternative is a session recording that spans a private
 * workspace surface.
 *
 * PostHog is intentionally untouched: it is Radar's product analytics, mounted
 * on every route by `AnalyticsGate` under the same consent policy, and
 * /platform/app behavior must not change.
 */
export function SitesPrivacyBoundary() {
  useEffect(() => {
    updateClarityConsent('denied', true)

    try {
      // stop() resolves asynchronously; nothing here depends on completion.
      void Sentry.getReplay()?.stop()
    } catch {
      // Replay is optional instrumentation; never let it break the page.
    }
  }, [])

  return null
}
